import React, { useCallback, useEffect, useState } from "react";
import { FaPlus, FaFilePdf, FaTrash } from "react-icons/fa";
import DataTable from "../../../components/DataTable";
import EmptyState from "../../../components/EmptyState";
import StatusBadge from "../../../components/StatusBadge";
import { useAppDialog } from "../../../components/AppDialog";
import { colors, spacing, typography, btnPrimary, btnGhost } from "../../../utils/designTokens";
import { comunicacionAdmin, mensajeDeError, motivoDeError, codigoDeError } from "../../../utils/comunicaciones/api";
import { ESTADOS, formatoFecha } from "../../../utils/comunicaciones/contenido";
import ComunicacionEditor from "./ComunicacionEditor";
import ComunicacionEstado from "./ComunicacionEstado";

// Pestaña "Comunicaciones" de Novedades: comunicados formales en PDF que le
// llegan a cada persona por WhatsApp y email (las Novedades se leen en la app).
// Props: buildingId, edificioNombre, showToast; vista/onVista si la controla la
// pantalla de Novedades (que pone "Nueva comunicación" en su encabezado) y
// onDisponible(bool) para que la esconda si el edificio no lo tiene.

// La función no está publicada (o no responde): el SDK lo reporta como
// "internal", "not-found" o "unavailable", sin mensaje del servidor.
const NO_DISPONIBLE = new Set(["internal", "not-found", "unavailable", "unimplemented", "deadline-exceeded"]);

export default function ComunicacionesAdmin({ buildingId, edificioNombre, showToast, vista: vistaProp, onVista, onDisponible }) {
  const { confirmDialog } = useAppDialog();
  const [vistaLocal, setVistaLocal] = useState({ tipo: "lista" }); // lista | editor {id} | estado {id}
  const vista = vistaProp || vistaLocal;
  const setVista = onVista || setVistaLocal;
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(true);
  // null | "deshabilitado" (el edificio no lo tiene) | "no_disponible" (el servidor todavía no lo tiene)
  const [bloqueo, setBloqueo] = useState(null);
  useEffect(() => {
    if (onDisponible) onDisponible(!bloqueo);
  }, [bloqueo, onDisponible]);

  const cargar = useCallback(async () => {
    if (!buildingId) return;
    setCargando(true);
    try {
      const r = await comunicacionAdmin("listar", { buildingId });
      setLista(r.comunicaciones || []);
      setBloqueo(null);
    } catch (e) {
      if (motivoDeError(e) === "deshabilitado") setBloqueo("deshabilitado");
      else if (NO_DISPONIBLE.has(codigoDeError(e))) setBloqueo("no_disponible");
      else showToast && showToast(mensajeDeError(e), "error");
    } finally {
      setCargando(false);
    }
  }, [buildingId, showToast]);

  // Al cambiar de edificio, vuelve a la lista.
  useEffect(() => {
    setVista({ tipo: "lista" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId]);

  useEffect(() => {
    if (vista.tipo === "lista") cargar();
  }, [cargar, vista.tipo]);

  const volver = () => setVista({ tipo: "lista" });

  const eliminar = async (fila) => {
    if (!(await confirmDialog(`¿Eliminar el borrador "${fila.asunto || "sin asunto"}"?`, { title: "Eliminar borrador", confirmLabel: "Eliminar", danger: true }))) return;
    try {
      await comunicacionAdmin("eliminar", { id: fila.id });
      cargar();
    } catch (e) {
      showToast && showToast(mensajeDeError(e), "error");
    }
  };

  if (vista.tipo === "editor") {
    return (
      <ComunicacionEditor
        key={vista.id || "nueva"}
        buildingId={buildingId}
        edificioNombre={edificioNombre}
        id={vista.id || null}
        onVolver={volver}
        onEnviada={(id) => setVista({ tipo: "estado", id })}
        showToast={showToast}
      />
    );
  }
  if (vista.tipo === "estado") return <ComunicacionEstado id={vista.id} onVolver={volver} showToast={showToast} />;

  if (bloqueo === "deshabilitado") {
    return (
      <EmptyState
        icon={FaFilePdf}
        title="Las comunicaciones todavía no están habilitadas para este edificio"
        description="Pedíselas a soporte de Habitia. Mientras tanto podés seguir publicando novedades."
      />
    );
  }
  if (bloqueo === "no_disponible") {
    return (
      <EmptyState
        icon={FaFilePdf}
        title="Las comunicaciones todavía no están disponibles"
        description="El servicio no respondió. Probá de nuevo en un rato; mientras tanto podés seguir publicando novedades."
        action={{ label: "Reintentar", onClick: cargar }}
      />
    );
  }

  const columnas = [
    { key: "numero", label: "N°", width: "70px", render: (v) => (v ? v : "—") },
    { key: "asunto", label: "Asunto", render: (v, fila) => v || fila.titulo || "Sin asunto" },
    {
      key: "estado",
      label: "Estado",
      width: "150px",
      render: (v, fila) => {
        const e = ESTADOS[v] || ESTADOS.borrador;
        return (
          <span style={{ display: "inline-flex", gap: spacing[1], alignItems: "center", flexWrap: "wrap" }}>
            <StatusBadge status={e.status} label={e.label} size="sm" />
            {fila.dryRun && <StatusBadge status="dry_run" label="Prueba" size="sm" />}
          </span>
        );
      },
    },
    { key: "fecha", label: "Fecha", width: "110px", render: (_, fila) => formatoFecha(fila.enviadaEnMs || fila.actualizadaEnMs || fila.creadaEnMs) },
    {
      key: "resumen",
      label: "Llegó",
      width: "200px",
      render: (r) =>
        r ? (
          <span style={S.resumen}>
            {r.wa.entregado + r.wa.leido} por WhatsApp · {r.mail.enviado} por email{r.pendientes ? ` · ${r.pendientes} en cola` : ""}
          </span>
        ) : (
          "—"
        ),
    },
    {
      key: "acciones",
      label: "",
      width: "60px",
      align: "right",
      render: (_, fila) =>
        fila.estado === "borrador" ? (
          <button
            type="button"
            style={{ ...btnGhost, padding: "6px 8px" }}
            className="habitia-btn-ghost"
            onClick={(ev) => {
              ev.stopPropagation();
              eliminar(fila);
            }}
            aria-label={`Eliminar el borrador ${fila.asunto || ""}`.trim()}
          >
            <FaTrash size={12} />
          </button>
        ) : null,
    },
  ];

  return (
    <div>
      {/* Suelta (sin la pantalla de Novedades), el botón va acá; dentro de Novedades va en su encabezado. */}
      {!onVista && (
        <div style={S.barra}>
          <button type="button" style={btnPrimary} className="habitia-btn-primary" onClick={() => setVista({ tipo: "editor" })}>
            <FaPlus size={13} /> Nueva comunicación
          </button>
        </div>
      )}
      <DataTable
        columns={columnas}
        data={lista}
        onRowClick={(fila) => setVista({ tipo: fila.estado === "borrador" ? "editor" : "estado", id: fila.id })}
        emptyIcon={FaFilePdf}
        emptyTitle={cargando ? "Cargando…" : "Todavía no hay comunicaciones"}
        emptyDescription={cargando ? undefined : "Armá la primera con Habi: le contás los puntos y te devuelve el PDF listo para revisar."}
      />
    </div>
  );
}

const S = {
  barra: { display: "flex", justifyContent: "flex-end", marginBottom: spacing[4] },
  resumen: { fontSize: typography.sizes.sm, color: colors.gray600 },
};
